import { injectable } from 'inversify';
import type { ICameraController, ICameraInfoApi } from '@/engine/app/interfaces';
import {
  cameraDirectionFromFluoroAngles,
  fluoroAnglesFromCamera,
  fluoroSCurveFromPlane,
  makeRotationMatrix
} from '@/engine/utils/geometryHelper';
import { action, makeObservable, observable } from 'mobx';
import type { FluoroAngles, IFluoroCurveController, InvertibleFluoroAngles } from './interfaces';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import * as THREE from 'three';

export class NullFluoroCurveController implements IFluoroCurveController {
  public readonly current: InvertibleFluoroAngles;

  public readonly data: Array<FluoroAngles>;

  public constructor() {
    this.current = {
      raoLao: 0,
      caudCran: 0,
      isInverted: false
    };
    this.data = [];
  }

  public dispose(): void {
    // noop
  }

  public isActive(): boolean {
    return false;
  }

  public setCamera(): void {
    // noop
  }
}

@injectable()
export class FluoroCurveController implements IFluoroCurveController {
  @observable public current: InvertibleFluoroAngles;

  @observable public data: Array<FluoroAngles>;

  private readonly _cameraController: ICameraController;

  private readonly _subscriptions: DisposablesBasket;

  private _planeOrigin?: THREE.Vector3;

  private _planeNormal?: THREE.Vector3;

  public constructor(
    cameraController: ICameraController,
    planningAlgorithm: PlanningAlgorithm,
    planeDescriptor: string
  ) {
    this.current = {
      raoLao: 0,
      caudCran: 0,
      isInverted: false
    };
    this.data = [];

    this._cameraController = cameraController;

    this._subscriptions = new DisposablesBasket();

    this._subscriptions.add(
      this._cameraController.onCameraModified((cameraInfo: ICameraInfoApi): void =>
        this._onCameraModified(cameraInfo.getCameraDirection())
      )
    );
    this._onCameraModified(this._cameraController.getCameraDirection());

    this._subscriptions.add(
      planningAlgorithm.addOnChangedCallbackFor<DT.Plane>(planeDescriptor, (plane?: DT.Plane) =>
        this._onPlaneChanged(plane)
      )
    );
    this._onPlaneChanged(planningAlgorithm.getValue(planeDescriptor, DT.Plane));

    makeObservable(this);
  }

  public dispose(): void {
    this._subscriptions.dispose();
  }

  public isActive(): boolean {
    return true;
  }

  public setCamera(raoLao: number, caudCran: number): void {
    if (this._planeNormal && this._planeOrigin) {
      const transform = new THREE.Matrix4();

      const cameraDirectionFrom = this._cameraController.getCameraDirection();
      const planeNormalTo = this._planeNormal;

      if (Math.abs(cameraDirectionFrom.angleTo(planeNormalTo) - Math.PI / 2) > 1e-6) {
        // Avoid rotations within accuracy that doe not make sense
        const planeNormalFrom = cameraDirectionFrom.clone().cross(planeNormalTo).cross(cameraDirectionFrom);
        const makePlanePerpendicular = makeRotationMatrix(
          this._planeOrigin,
          planeNormalFrom.clone().cross(planeNormalTo),
          planeNormalFrom.angleTo(planeNormalTo)
        );
        cameraDirectionFrom.transformDirection(makePlanePerpendicular);
        transform.copy(makePlanePerpendicular);
      }

      const cameraDirectionTo = cameraDirectionFromFluoroAngles(raoLao, caudCran, this._planeNormal).negate();
      const inPlaneRotationAngle = cameraDirectionFrom.angleTo(cameraDirectionTo);
      if (Math.abs(inPlaneRotationAngle - Math.PI) < 1e-6) {
        // Corner case: when rotation is from this vector to opposite => 180 degrees rotation.
        // Cross product is of the zero length thus rotation plane is ambiguous.
        const rotateInPlane = makeRotationMatrix(this._planeOrigin, this._planeNormal, Math.PI);
        transform.premultiply(rotateInPlane);
      } else if (inPlaneRotationAngle > 1e-6) {
        // Avoid rotations within accuracy that dos not make sense
        const rotateInPlane = makeRotationMatrix(
          this._planeOrigin,
          cameraDirectionFrom.clone().cross(cameraDirectionTo),
          inPlaneRotationAngle
        );
        transform.premultiply(rotateInPlane);
      }

      this._cameraController.transformCamera(transform);
    }
  }

  @action
  private _onCameraModified(cameraDirection: THREE.Vector3): void {
    const fluoroAngles = fluoroAnglesFromCamera(cameraDirection.negate());
    let isInverted = false;
    if (Math.abs(fluoroAngles.raoLao) > 90 + 1e-4 || Math.abs(fluoroAngles.caudCran) > 90 + 1e-4) {
      // Tha way to draw Red pointer on graph - via reversal of view vector.
      fluoroAngles.raoLao = fluoroAngles.raoLao - (fluoroAngles.raoLao > 0 ? 180 : -180);
      fluoroAngles.caudCran = fluoroAngles.caudCran - (fluoroAngles.caudCran > 0 ? 180 : -180);
      isInverted = true;
    }
    if (Math.abs(Math.abs(fluoroAngles.raoLao) - 90) < 1e-4) {
      // Corner case: we don't want pointer to be invisible because of accuracy.
      // Graph is showing values strictly within [-90..90] range, so 90.0000000001 will be invisible, let's avoid  this.
      fluoroAngles.raoLao = fluoroAngles.raoLao > 0 ? 90 : -90;
    }
    if (Math.abs(Math.abs(fluoroAngles.caudCran) - 90) < 1e-4) {
      // Corner case: we don't want pointer to be invisible because of accuracy.
      // Graph is showing values strictly within [-90..90] range, so 90.0000000001 will be invisible, let's avoid  this.
      fluoroAngles.caudCran = fluoroAngles.caudCran > 0 ? 90 : -90;
    }
    this.current.raoLao = fluoroAngles.raoLao;
    this.current.caudCran = fluoroAngles.caudCran;
    this.current.isInverted = isInverted;
  }

  @action
  private _onPlaneChanged(plane?: DT.Plane): void {
    if (!plane) {
      this.data = [];
    } else {
      this._planeOrigin = plane.value.origin.clone();
      this._planeNormal = plane.value.planeEq.normal.clone();
      this.data = fluoroSCurveFromPlane(this._planeOrigin, this._planeNormal, 20);
    }
  }
}
